import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  UserPlus, MagnifyingGlass, Phone, EnvelopeSimple, Buildings,
  Trash, DownloadSimple, CheckCircle, XCircle, Clock, Funnel
} from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";

export default function AdminLeadsPage() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(res.data || []);
    } catch { toast.error("Failed to load leads"); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l));
      toast.success(`Lead marked as ${status}`);
    } catch { toast.error("Failed to update"); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(prev => prev.filter(l => l.id !== id));
      toast.success("Lead deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const exportCSV = () => {
    window.open(`${API}/admin/export/leads`, "_blank");
  };

  const filtered = leads.filter(l => {
    const matchSearch = !search || [l.full_name, l.email, l.phone, l.category, l.chapter_name].some(f => f?.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = statusFilter === "all" || l.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const statusColors = {
    new: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    contacted: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
    converted: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    rejected: "bg-red-500/10 text-red-500 border-red-500/20",
  };

  const statusIcons = {
    new: <Clock className="w-3.5 h-3.5" weight="fill" />,
    contacted: <Phone className="w-3.5 h-3.5" weight="fill" />,
    converted: <CheckCircle className="w-3.5 h-3.5" weight="fill" />,
    rejected: <XCircle className="w-3.5 h-3.5" weight="fill" />,
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <>
      <SEO title="Lead Management" description="Manage express interest leads." path="/admin/leads" noIndex />
      <div className="space-y-6" data-testid="admin-leads-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-primary flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-white" weight="fill" />
              </div>
              Lead Management
            </h1>
            <p className="text-muted text-sm mt-1">{filtered.length} leads</p>
          </div>
          <Button onClick={exportCSV} variant="outline" className="border-bch-gold/30 text-bch-gold hover:bg-bch-gold/5" data-testid="export-leads-btn">
            <DownloadSimple className="w-4 h-4 mr-2" weight="bold" /> Export CSV
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <Input placeholder="Search by name, email, phone..." value={search} onChange={e => setSearch(e.target.value)}
              className="bg-surface border-[var(--border-color)] text-primary pl-10" data-testid="leads-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] bg-surface border-[var(--border-color)] text-primary" data-testid="leads-status-filter">
              <Funnel className="w-4 h-4 mr-2 text-muted" />
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent className="bg-card border-[var(--border-color)]">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Leads Table */}
        <div className="bg-card border border-[var(--border-color)] rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-color)] bg-surface/50">
                  <th className="text-left px-5 py-3.5 text-muted font-semibold text-xs uppercase tracking-wider">Name</th>
                  <th className="text-left px-5 py-3.5 text-muted font-semibold text-xs uppercase tracking-wider">Contact</th>
                  <th className="text-left px-5 py-3.5 text-muted font-semibold text-xs uppercase tracking-wider">Category</th>
                  <th className="text-left px-5 py-3.5 text-muted font-semibold text-xs uppercase tracking-wider">Chapter</th>
                  <th className="text-left px-5 py-3.5 text-muted font-semibold text-xs uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3.5 text-muted font-semibold text-xs uppercase tracking-wider">Date</th>
                  <th className="text-right px-5 py-3.5 text-muted font-semibold text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-muted">No leads found</td></tr>
                ) : filtered.map(l => (
                  <tr key={l.id} className="border-b border-[var(--border-color)] hover:bg-surface/30 transition-colors" data-testid={`lead-row-${l.id}`}>
                    <td className="px-5 py-4">
                      <p className="text-primary font-medium">{l.full_name}</p>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-primary text-xs flex items-center gap-1"><EnvelopeSimple className="w-3.5 h-3.5 text-muted" />{l.email}</p>
                      <p className="text-muted text-xs flex items-center gap-1 mt-0.5"><Phone className="w-3.5 h-3.5" />{l.phone}</p>
                    </td>
                    <td className="px-5 py-4 text-secondary text-xs">{l.category || l.business_category || "—"}</td>
                    <td className="px-5 py-4 text-secondary text-xs">{l.chapter_name || "—"}</td>
                    <td className="px-5 py-4">
                      <Select value={l.status || "new"} onValueChange={v => updateStatus(l.id, v)}>
                        <SelectTrigger className={`h-7 text-xs font-semibold border rounded-full px-3 w-[120px] ${statusColors[l.status || "new"]}`} data-testid={`lead-status-${l.id}`}>
                          <span className="flex items-center gap-1.5">{statusIcons[l.status || "new"]}<SelectValue /></span>
                        </SelectTrigger>
                        <SelectContent className="bg-card border-[var(--border-color)]">
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-4 text-muted text-xs">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => deleteLead(l.id)} className="text-red-400 hover:text-red-500 p-1" data-testid={`delete-lead-${l.id}`}>
                        <Trash className="w-4 h-4" weight="bold" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
